import React from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { isTruss } from '@paragon/contract-library';
import { getPlatePairPlate1Matrix, getPlatePairPlate2Matrix } from '@paragon/three-shared';

import { useAppSelector } from '../../../components/hooks';
import { calculateTrussPlyMatrices } from '../../../library/layout-geometry/truss-envelopes/calculateTrussPlyMatrices';
import { getCommittedComponentDesigns } from '../../../store/selectors/getComponentDesignsSelectors';
import { getVisibleTrussEnvelopes } from '../../../store/selectors/layoutDataSelectors';
import { getAllTrussEnvelopePositions } from '../../../store/selectors/layoutDocumentSelectors';
import { is2DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getActiveEnvelopeMode } from '../../../store/selectors/layoutVisualOptionsSelectors';
import { getShapeKey } from '../../util/PlateGeometryCache';
import { BulkPlatePairsController, PlateForBulkRendering } from './BulkPlatePairsController';

const getAllVisiblePlates = createSelector(
  getVisibleTrussEnvelopes,
  getCommittedComponentDesigns,
  getAllTrussEnvelopePositions,

  (trussEnvelopes, componentDesigns, trussEnvelopeMatrices): PlateForBulkRendering[] => {
    const result: PlateForBulkRendering[] = [];

    for (const trussEnvelope of Object.values(trussEnvelopes)) {
      if (trussEnvelope.componentDesignGuid == null) continue;

      const trussEnvelopeGuid = trussEnvelope.guid;
      const trussEnvelopeMatrix = trussEnvelopeMatrices[trussEnvelopeGuid];

      const componentDesign = componentDesigns.find((design) => design.guid === trussEnvelope.componentDesignGuid);

      if (!trussEnvelopeMatrix || !componentDesign || !isTruss(componentDesign.component)) continue;

      const plyMatrices = calculateTrussPlyMatrices(componentDesign.component, trussEnvelope, trussEnvelopeMatrix);

      for (const plyMatrix of plyMatrices) {
        for (const platePair of componentDesign.component.platePairs) {
          const plateShapeKey = getShapeKey(platePair);
          const plate1Matrix = getPlatePairPlate1Matrix(platePair).premultiply(plyMatrix);
          const plate2Matrix = getPlatePairPlate2Matrix(platePair).premultiply(plyMatrix);

          result.push({ trussEnvelopeGuid, plateShapeKey, matrix: plate1Matrix });
          result.push({ trussEnvelopeGuid, plateShapeKey, matrix: plate2Matrix });
        }
      }
    }

    return result;
  },
);

export function LayoutPlatePairsVisual() {
  const cameraIs2D = useAppSelector(is2DLayoutCameraInUse);
  const envelopeMode = useAppSelector(getActiveEnvelopeMode);

  const positionedPlates = useAppSelector(getAllVisiblePlates);
  const plateTypeProperties = useAppSelector((state) => state.plateProperties.plateTypeProperties);

  if (cameraIs2D || envelopeMode !== 'components' || positionedPlates.length === 0) return null;

  // sometimes this visual is rendered before plateTypeProperties are loaded
  if (Object.keys(plateTypeProperties).length === 0) return null;

  return <BulkPlatePairsController data={positionedPlates} plateTypePropertiesDictionary={plateTypeProperties} />;
}
